'use client';

import { useId } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';

import { Avatar } from '@/components/ui/avatar';
import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * Müşteri yorumları — altı sesten bir duvar.
 *
 * Sayfadaki yeri: "Kontrol sende"in ALTI, fiyatın ÜSTÜ. O bölüm "karar hâlâ
 * sende" dedi; kullanıcının kalan tek sorusu "peki başkası denemiş mi". Fiyatı
 * görmeden önceki son güven adımı burası.
 *
 * ── Neden duvar, neden öne çıkan bir kart ─────────────────────────────────
 *
 * Bölüm iki kez elden geçti (12 Ağu 2026). Önce üç sütun düz metindi
 * ("çok sade"), sonra üç eşit kart oldu — o da yetmedi: altı eşit kart bir
 * ızgara doldurur ama hiçbir şey söylemez, göz nereden başlayacağını bilemez.
 *
 * Şimdi bir HİYERARŞİ var. İlk yorum "öne çıkan": koyu zeminde, iri puntoyla,
 * masaüstünde iki sütun × iki satır kaplıyor. Kalan beşi onun etrafına
 * diziliyor. Okuyucu önce tek bir güçlü referansı okuyor, sonra gözü çevredeki
 * beşte "demek yalnız bir kişi değil" diye geziniyor. Altı eşit kartta bu iki
 * adım yok, tek bir bulanık kalabalık var.
 *
 * Koyu yüzey sayfanın kendi dilinden: hero bloğu ve kapanış şeridi de aynı
 * rampayı taşıyor (globals.css → "Kapanış CTA"). Beyaz bölümün ortasındaki tek
 * koyu kutu yeni bir malzeme değil, sayfanın zaten konuştuğu vurgu.
 *
 * ── Veri: tek kaynak, messages/*.json ─────────────────────────────────────
 *
 * Bu dosyada TEK BİR yorum metni yok. Alıntı, isim, görev, şirket, logo ve
 * (varsa) fotoğraf dosya adı `testimonials.items` dizisinde. Yanındaki `_DEMO`
 * notu neyin yer tutucu olduğunu, neyin yazılmayacağını (rakamlı iddia,
 * schema.org Review işaretlemesi, yıldızlı puan) ve iki yapısal kuralı sayıyor:
 * dizinin İLK öğesi öne çıkan karttır, ve üç sütunlu duvar altı ya da dokuz
 * yorumda tam doluyor.
 *
 * ── Avatar ────────────────────────────────────────────────────────────────
 *
 * İmzanın başında `Avatar` (components/ui/avatar). Şu an FOTOĞRAF YOK, baş
 * harfler görünüyor — ve bu bilinçli: veri demo, gerçek olmayan kişilere stok
 * portre takmak "bunlar gerçek müşteri" demenin görsel hâli olurdu. Slot hazır:
 * `items[].avatar` alanına dosya adı yazıldığı anda (dosya `public/avatars/`
 * içine) fotoğraf baş harflerin üstüne biner. Dosya eksikse baş harfler kalır —
 * geri düşüş bileşende, JavaScript'siz.
 *
 * Öne çıkan kartta avatar bir tık büyük ve koyu zemine göre ters çevriliyor
 * (`avatar--on-dark`): koyu daire koyu kutuda kaybolurdu.
 *
 * ── Marka şeridiyle ilişki ────────────────────────────────────────────────
 *
 * Altı şirket, anasayfanın üstündeki marka şeridindeki altı markanın TAMAMI ve
 * logolar aynı dosyalardan geliyor. Tek fark mürekkep: şeritte ve öne çıkan
 * kartta açık ton, beyaz kartlarda koyu ton. svgl'den inen dosyalar koyu zemin
 * varyantı (yazı kısmı beyaz) ve maske olarak kullanıldıkları için kendi
 * renkleri hiç devrede değil — hangi zemine düşerse ona göre boyanıyorlar.
 * Şeritteki hover'ın karşılığı burada yok; hücre tıklanabilir değil, hover
 * afordansı yanlış söz verirdi.
 *
 * ── Hareket ne anlatıyor ──────────────────────────────────────────────────
 *
 * Kartlar sırayla yerine oturuyor, her kartın içi de okuma sırasıyla doluyor:
 * logo → alıntı → ayraç çizgisi → imza. Yani "kim → ne dedi → (çizgi çekilir) →
 * tam olarak kim". Ayraç soldan sağa ÇİZİLİYOR (`scaleX`, origin sol).
 *
 * Blur YALNIZCA öne çıkan alıntıda. Altı alıntının hepsine verilseydi hem §6'nın
 * bütçesi gereksiz yere harcanırdı hem de "odaklanılan şey" iddiası anlamını
 * yitirirdi — her şey vurguluysa hiçbir şey vurgulu değildir. Kalan beş alıntı
 * yalnızca yerine oturuyor.
 *
 * Kartlar arası gecikme kasten kısa (`stagger.tight`): altı ses sırayla değil
 * neredeyse birlikte geliyor. Toplam 6 × 0,04 + 0,05 + 3 × 0,04 ≈ 0,41 sn,
 * DESIGN.md §5'in 1,2 sn sınırının çok altında.
 *
 * Sonsuz döngü ve parallax yok, dolayısıyla `useReducedMotion` ile elle
 * kapatılacak bir hareket de yok; kök layout'taki <MotionConfig
 * reducedMotion="user"> tamamını karşılıyor (DESIGN.md §1) — ölçüldü: tercih
 * açıkken bütün öğeler opaklık 1'de, ayraç tam genişlikte oturuyor.
 *
 * ⚠️ Hover'da kart YÜKSELMİYOR (transform yok), yalnızca çerçevesi/gölgesi
 * değişiyor. Sebep teknik: Motion animasyonladığı özelliği öğenin `style`'ına
 * YAZAR ve `y` animasyonu bittiğinde `transform` inline kalır — CSS'teki
 * `:hover { transform: … }` satır içi stile yenilir ve hover sessizce hiçbir şey
 * yapmaz. Kaldırma isteniyorsa hareketin bir iç sarmalayıcıya taşınması gerekir;
 * ama `<figcaption>` figürün doğrudan çocuğu olmak zorunda olduğu için o
 * sarmalayıcı buraya konamıyor.
 *
 * ── Belge yapısı ──────────────────────────────────────────────────────────
 *
 * Her yorum bir <figure>: içinde <blockquote> ve <figcaption>. Alıntı da isim
 * de görev de gerçek metin — hiçbiri görsele gömülü değil, ekran okuyucu ve
 * arama motoru ikisi de okuyor. <figcaption> figürün SON çocuğu olmak zorunda
 * (HTML kuralı); ayraç çizgisi bu yüzden figcaption'ın `border-top`'u değil,
 * ondan ÖNCE gelen ayrı bir <span> — çizilebilmesi için kendi öğesi olmalı.
 *
 * Öne çıkan kartın görsel ayrıcalığı belgede bir sıra iddiası taşımıyor: altı
 * figure de aynı düzeyde, aralarında başlık ya da liste hiyerarşisi yok. Ekran
 * okuyucu altı yorumu eşit sırayla okuyor.
 */

type Voice = {
  quote: string;
  name: string;
  role: string;
  company: string;
  logo: string;
  /** public/avatars/ altındaki dosya adı, uzantısıyla. Yoksa baş harfler. */
  avatar?: string;
};

/**
 * Kutu eşitliğinden sonra kalan optik farkın payı — marka şeridiyle aynı
 * ölçüm, aynı değerler (components/brands.tsx içindeki uzun gerekçe orada).
 * Aynı 5:1 kutu kullanıldığı için aynı sayılar geçerli. Listede olmayan bir
 * slug 1 alır; ±%10 dışına çıkma, kutu eşitliği bozulur.
 */
const LOGO_SCALE: Record<string, number> = {
  shopify: 1.06,
  webflow: 1,
  dropbox: 1,
  coinbase: 1.04,
  asana: 1,
  nvidia: 0.96,
};

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Başlık bloğu tek parça girer — etiket, başlık ve giriş bölünmez. */
const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: stagger.tight } },
};

/* Kart hem yerine oturuyor hem içindekilerin sırasını tutuyor: yay bittiğinde
   değil, kart yola çıktıktan 0,05 sn sonra içerisi dolmaya başlıyor — kart boş
   bir kutu olarak durmuyor. */
const cardV: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      ...springFade(spring.default),
      staggerChildren: stagger.tight,
      delayChildren: 0.05,
    },
  },
};

const logoV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/* Bölümdeki tek blur, yalnızca öne çıkan alıntıda: göz oraya odaklanıyor. */
const quoteLeadV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

const quoteV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Ayraç soldan sağa çiziliyor (transform-origin CSS'te sol). Yükseklik 1px
   olduğu için yalnızca scaleX görünür bir iş yapıyor. */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: springFade(spring.default) },
};

const citeV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function Testimonials() {
  const t = useTranslations('testimonials');
  const locale = useLocale();
  const uid = useId();

  const voices = (t.raw('items') ?? []) as Voice[];
  if (voices.length === 0) return null;

  return (
    <section className="voice" aria-labelledby={`${uid}-title`}>
      {/* Yan sütunlar basılmıyor — çapraz tarama sayfadan kaldırıldı
          (kullanıcı kararı). .grid-item-8fr `grid-column: 2` ile kendi
          yerleşiyor; görünen kenar .voice-inner'ın dikey hatları. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="voice-inner" variants={sectionV} initial={false} animate="show">
            <motion.div className="voice-head" variants={headV}>
              <p className="voice-eyebrow">{t('eyebrow')}</p>
              <h2 className="voice-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="voice-lead">{t('lead')}</p>
            </motion.div>

            <motion.div className="voice-grid" variants={gridV}>
              {voices.map((voice, i) => {
                /* Öne çıkan kart dizinin ilk öğesi — yerleşim değil VERİ kararı;
                   hangi referansın öne çıkacağını içerik yazan seçiyor. */
                const lead = i === 0;

                return (
                  <motion.figure
                    className={lead ? 'voice-cell voice-cell--lead' : 'voice-cell'}
                    key={`${voice.logo}-${i}`}
                    variants={cardV}
                  >
                    {/* Dekoratif: şirket adı zaten imzada metin olarak var,
                        logo ikinci kez okunmasın. */}
                    <motion.span
                      className="voice-logo"
                      aria-hidden="true"
                      variants={logoV}
                      style={
                        {
                          '--logo-src': `url("/logos/brands/${voice.logo}.svg")`,
                          '--logo-scale': LOGO_SCALE[voice.logo] ?? 1,
                        } as CSSProperties
                      }
                    >
                      <span className="voice-logo-mono" />
                    </motion.span>

                    <motion.blockquote
                      className="voice-quote"
                      variants={lead ? quoteLeadV : quoteV}
                    >
                      <p className="voice-quote-text">{voice.quote}</p>
                    </motion.blockquote>

                    {/* figcaption'ın border'ı DEĞİL ayrı öğe: çizilebilmesi
                        için kendi transform'unu taşıması gerekiyor. */}
                    <motion.span className="voice-rule" aria-hidden="true" variants={ruleV} />

                    <motion.figcaption className="voice-cite" variants={citeV}>
                      <Avatar
                        className={lead ? 'voice-avatar avatar--on-dark' : 'voice-avatar'}
                        locale={locale}
                        name={voice.name}
                        src={voice.avatar ? `/avatars/${voice.avatar}` : undefined}
                      />
                      <span className="voice-cite-text">
                        <span className="voice-name">{voice.name}</span>
                        <span className="voice-role">
                          {voice.role}
                          {/* Ayraç dekoratif: ekran okuyucu "orta nokta" demesin,
                              iki bilgi arka arkaya okunsun. */}
                          <span aria-hidden="true"> · </span>
                          {voice.company}
                        </span>
                      </span>
                    </motion.figcaption>
                  </motion.figure>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
